<template>
	<div>
        <v-alert v-if="loadError" type="error" dense outlined>{{ lang.server_not_responding }}</v-alert>
		<h6 class="module-title">{{ lang.nd_finance_module }}</h6>

		<b-row class="mb-3 justify-content-center">
			<b-col
				v-for="card in financeKpiCards"
				:key="card.key"
				cols="12"
				md="6"
				lg="4"
				xl="2"
			>
				<v-card flat class="kpi-card" :class="card.className">
					<div class="kpi-label">{{ card.label }}</div>
					<div class="kpi-value">{{ formatCurrency(card.value) }}</div>
				</v-card>
			</b-col>
		</b-row>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_payment_types || 'Payment Types' }}</h6>
			</div>

			<div class="payment-types-line" :style="{ '--payment-count': Math.max(paymentTypeCards.length, 1) }">
				<v-card
					v-for="payment in paymentTypeCards"
					:key="payment.id"
					flat
					class="payment-card"
					:class="payment.balanceClass"
					:style="paymentCardStyle"
				>
					<div class="payment-title">{{ payment.name }}</div>
					<div class="payment-row">
						<span>{{ lang.nd_income || 'Income' }}</span>
						<strong>{{ formatCurrency(payment.income) }}</strong>
					</div>
					<div class="payment-row">
						<span>{{ lang.nd_expense || 'Expense' }}</span>
						<strong>{{ formatCurrency(payment.expense) }}</strong>
					</div>
					<div class="payment-row payment-row--balance">
						<span>{{ lang.nd_balance || 'Balance' }}</span>
						<strong>{{ formatCurrency(payment.balance) }}</strong>
					</div>
				</v-card>
			</div>
		</v-card>

		<v-card flat class="section-card mb-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_income_by }} {{ timeGrainLabel }}</h6>
				<v-chip small outlined color="primary">
					{{ incomePeriods.length }} {{ lang.nd_intervals }}
				</v-chip>
			</div>

			<v-data-table
				:headers="incomeTableHeaders"
				:items="incomeTableRows"
				dense
				hide-default-footer
				class="elevation-0 finance-table"
			>
				<template v-slot:item="{ item, headers }">
					<tr>
						<td
							v-for="header in headers"
							:key="header.value"
						>
							<span v-if="header.value === 'branch'">{{ item[header.value] }}</span>
							<span v-else>{{ formatCurrency(item[header.value]) }}</span>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>

		<b-row>
			<b-col cols="12" lg="4" class="mb-3 mb-lg-0">
				<v-card flat class="section-card h-100">
					<div class="section-head">
						<h6 class="section-title mb-0">{{ lang.nd_expense_breakdown }}</h6>
						<v-chip color="error" dark small>
							{{ lang.nd_total }}: {{ formatCurrency(totalExpenses) }}
						</v-chip>
					</div>

					<div
						v-for="expense in expenseBreakdown"
						:key="expense.type"
						class="expense-row"
					>
						<div class="d-flex justify-content-between align-items-center mb-1">
							<span class="expense-type">{{ expense.type }}</span>
							<span class="expense-value">{{ formatCurrency(expense.amount) }}</span>
						</div>
						<v-progress-linear
							:value="expense.percentage"
							height="7"
							color="error"
							background-color="grey lighten-3"
							rounded
						/>
					</div>
				</v-card>
			</b-col>

			<b-col cols="12" lg="8">
				<v-card flat class="section-card h-100">
					<div class="section-head">
						<h6 class="section-title mb-0">{{ lang.nd_employee_financial_cards }}</h6>
						<v-chip small outlined color="primary">
							{{ employeeFinanceCards.length }} {{ lang.nd_employees }}
						</v-chip>
					</div>

					<b-row>
						<b-col
							v-for="employee in employeeFinanceCards"
							:key="employee.id"
							cols="12"
							md="6"
							xl="4"
						>
							<v-card outlined class="employee-card">
								<button type="button" class="employee-card-head" @click="toggleEmployeeCard(employee.id)">
									<div>
										<div class="employee-name">{{ employee.name }}</div>
										<div class="employee-branch">{{ employee.branchName }}</div>
									</div>
									<div class="employee-head-right">
										<div class="employee-total">{{ lang.nd_salary }}: <strong>{{ formatCurrency(employee.netPaidSalary) }}</strong></div>
										<v-icon small>{{ isEmployeeExpanded(employee.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
									</div>
								</button>

								<v-expand-transition>
									<div v-show="isEmployeeExpanded(employee.id)">
										<div class="d-flex justify-content-end mb-2">
											<v-chip
												small
												class="status-chip"
												:class="employee.idStatus.className"
											>
												{{ employee.idStatus.label }}
											</v-chip>
										</div>
										<div class="metric-row">
											<span>{{ lang.nd_salary }}</span>
											<strong>{{ formatCurrency(employee.salary) }}</strong>
										</div>
										<div class="metric-row">
											<span>{{ lang.nd_pay_advanced }}</span>
											<strong>{{ formatCurrency(employee.payAdvanced) }}</strong>
										</div>
										<div class="metric-row">
											<span>{{ lang.nd_deductions }}</span>
											<strong>{{ formatCurrency(employee.deductions) }}</strong>
										</div>
										<div class="metric-row">
											<span>{{ lang.nd_non_attendance }}</span>
											<strong>{{ formatCurrency(employee.nonAttendance) }}</strong>
										</div>
										<div class="metric-row">
											<span class="metric-label">
												{{ lang.nd_rewards }}
												<v-tooltip bottom>
													<template v-slot:activator="{ on, attrs }">
														<v-icon
															small
															class="metric-help-icon"
															v-bind="attrs"
															v-on="on"
														>
															mdi-information-outline
														</v-icon>
													</template>
													<span>{{ lang.nd_rewards_hint }}</span>
												</v-tooltip>
											</span>
											<strong>{{ formatCurrency(employee.rewards) }}</strong>
										</div>
										<div class="metric-row">
											<span>{{ lang.nd_overtime }}</span>
											<strong>{{ formatCurrency(employee.overtime) }}</strong>
										</div>
										<div class="metric-row metric-row--net">
											<span>{{ lang.nd_net_paid_salary }}</span>
											<strong>{{ formatCurrency(employee.netPaidSalary) }}</strong>
										</div>
									</div>
								</v-expand-transition>
							</v-card>
						</b-col>
					</b-row>
				</v-card>
			</b-col>
		</b-row>

		<v-card flat class="section-card mt-3">
			<div class="section-head">
				<h6 class="section-title mb-0">{{ lang.nd_branch_summary_income_expense }}</h6>
			</div>

			<div
				v-for="summary in branchFinancialSummary"
				:key="summary.branchId"
				class="summary-row"
			>
				<div class="summary-main">
					<div class="summary-branch">{{ summary.branchName }}</div>
					<div class="summary-values">
						<span>{{ lang.nd_income }}: {{ formatCurrency(summary.totalIncome) }}</span>
						<span>{{ lang.nd_expense }}: {{ formatCurrency(summary.totalExpense) }}</span>
					</div>
				</div>

				<div class="summary-indicator-wrap">
					<v-progress-linear
						:value="summary.expenseRatio"
						height="9"
						:color="summary.difference >= 0 ? 'success' : 'error'"
						background-color="grey lighten-3"
						rounded
					/>
					<v-chip
						small
						:color="summary.difference >= 0 ? 'success' : 'error'"
						dark
					>
						{{ summary.difference >= 0 ? lang.nd_profit : lang.nd_loss }}:
						{{ formatCurrency(Math.abs(summary.difference)) }}
					</v-chip>
				</div>
			</div>
		</v-card>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'FinanceModuleContent',
	props: {
		filters: {
			type: Object,
			required: true
		},
		branchOptions: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
            requestSerial: 0,
            loadError: false,
			incomeLoading: false,
			apiIncomePeriods: [],
			apiIncomeTableRows: [],
			apiExpenseBreakdown: [],
			apiTotalExpenses: null,
			apiBranchExpenseTotals: [],
			apiEmployeeFinanceCards: [],
			apiFinanceSummary: null,
			apiPaymentTypeSummary: [],
			expandedEmployeeIds: []
		};
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.fetchIncomeData();
			}
		}
	},
	mounted() {
		this.fetchIncomeData();
	},
	computed: {
		lang: function () {
			if (this.$cookies.get(this.$store.state.LangCooki) != null) {
				if (this.$cookies.get(this.$store.state.LangCooki) == 'en') {
					return this.$store.state.lang.en;
				} else if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') {
					return this.$store.state.lang.ur;
				} else {
					return this.$store.state.lang.ar;
				}
			}
			else if (this.$store.state.deflang == 'en') {
				return this.$store.state.lang.en;
			}
			else if (this.$store.state.deflang == 'ur') {
				return this.$store.state.lang.ur;
			} else {
				return this.$store.state.lang.ar;
			}
		},
		selectedBranchIds() {
			return this.filters.branches.length
				? this.filters.branches
				: this.branchOptions.map((branch) => branch.id);
		},
		selectedBranchObjects() {
			return this.branchOptions.filter((branch) =>
				this.selectedBranchIds.includes(branch.id)
			);
		},
		normalizedDateRange() {
			const now = new Date();
			const end = this.parseISODate(this.filters.endDate) || now;
			const start =
				this.parseISODate(this.filters.startDate) ||
				new Date(end.getFullYear(), end.getMonth(), 1);

			if (start <= end) {
				return { start, end };
			}

			return { start: end, end: start };
		},
		timeGrain() {
			const { start, end } = this.normalizedDateRange;
			const oneDay = 1000 * 60 * 60 * 24;
			const daysDiff = Math.floor((end - start) / oneDay);
			const monthSpan =
				(end.getFullYear() - start.getFullYear()) * 12 +
				(end.getMonth() - start.getMonth());

			if (monthSpan >= 1 || daysDiff > 31) {
				return 'monthly';
			}

			if (this.isSameWeek(start, end)) {
				return 'daily';
			}

			return 'weekly';
		},
		timeGrainLabel() {
			if (this.timeGrain === 'monthly') return this.lang.nd_month;
			if (this.timeGrain === 'weekly') return this.lang.nd_week;
			return this.lang.nd_day;
		},
		incomePeriods() {
			return this.apiIncomePeriods;
		},
		incomeTableHeaders() {
			const periodHeaders = this.incomePeriods.map((period) => ({
				text: period.label,
				value: period.key,
				align: 'end',
				sortable: false
			}));

			return [
				{ text: this.lang.nd_branch, value: 'branch', sortable: false },
				...periodHeaders,
				{ text: this.lang.nd_total, value: 'total', align: 'end', sortable: false }
			];
		},
		incomeTableRows() {
			return this.apiIncomeTableRows;
		},
		expenseBreakdown() {
			return this.apiExpenseBreakdown;
		},
		totalExpenses() {
			return Number(this.apiTotalExpenses || 0);
		},
		financeSummary() {
			const summary = this.apiFinanceSummary || {};
			return {
				totalIncome: Number(summary.totalIncome || 0),
				totalExpenses: Number(summary.totalExpenses || 0),
				totalSalaries: Number(summary.totalSalaries || 0),
				totalPurchase: Number(summary.totalPurchase || 0),
				totalRevenueOrLoss: Number(summary.totalRevenueOrLoss || 0)
			};
		},
		financeKpiCards() {
			const summary = this.financeSummary;
			const revClass = summary.totalRevenueOrLoss >= 0 ? 'kpi-card--profit' : 'kpi-card--loss';
			return [
				{ key: 'income', label: this.lang.nd_total_income || 'Total Income', value: summary.totalIncome, className: 'kpi-card--income' },
				{ key: 'expenses', label: this.lang.nd_total_expenses || 'Total Expenses', value: summary.totalExpenses, className: 'kpi-card--expense' },
				{ key: 'salaries', label: this.lang.nd_total_salaries || 'Total Salaries', value: summary.totalSalaries, className: 'kpi-card--salary' },
				{ key: 'purchase', label: this.lang.nd_total_purchase || 'Total Purchase', value: summary.totalPurchase, className: 'kpi-card--purchase' },
				{ key: 'revenue', label: this.lang.nd_revenue_or_loss || 'Revenue / Loss', value: summary.totalRevenueOrLoss, className: revClass }
			];
		},
		paymentTypeCards() {
			return this.apiPaymentTypeSummary.map((payment) => {
				const balance = Number(payment.balance || 0);
				let balanceClass = 'payment-card--even';
				if (balance > 0) balanceClass = 'payment-card--profit';
				if (balance < 0) balanceClass = 'payment-card--loss';

				return {
					id: Number(payment.id || 0),
					name: payment.name || this.lang.nd_unknown,
					income: Number(payment.income || 0),
					expense: Number(payment.expense || 0),
					balance,
					balanceClass
				};
			});
		},
		paymentCardStyle() {
			const count = Math.max(this.paymentTypeCards.length, 1);
			return {
				flex: `1 1 calc(100% / ${count})`,
				minWidth: '180px'
			};
		},
		employeeFinanceCards() {
			return this.apiEmployeeFinanceCards
				.filter((employee) => !this.selectedBranchIds.length || this.selectedBranchIds.includes(employee.branchId))
				.map((employee) => {
					return {
						...employee,
						branchName: employee.branchName || this.lang.nd_unknown_branch,
						idStatus: this.getIdExpiryMeta(employee.idExpiryDate)
					};
				});
		},
		branchFinancialSummary() {
			const expenseTotalsByBranch = {};
			if (this.apiBranchExpenseTotals.length) {
				this.apiBranchExpenseTotals.forEach((row) => {
					const branchId = Number(row.branchId || row.branchid || 0);
					if (branchId > 0) {
						expenseTotalsByBranch[branchId] = Number(row.totalExpense || row.total_expense || 0);
					}
				});
			}

			const branchMap = {};
			this.incomeTableRows.forEach((row) => {
				const branchId = Number(row.branchId || 0);
				if (branchId > 0) {
					branchMap[branchId] = row.branch || this.lang.nd_unknown_branch;
				}
			});
			this.apiBranchExpenseTotals.forEach((row) => {
				const branchId = Number(row.branchId || row.branchid || 0);
				if (branchId > 0 && !branchMap[branchId]) {
					branchMap[branchId] = row.branch || this.lang.nd_unknown_branch;
				}
			});

			return Object.keys(branchMap).map((branchKey) => {
				const branchId = Number(branchKey);
				const incomeRow = this.incomeTableRows.find((row) => Number(row.branchId || 0) === branchId);
				const totalIncome = incomeRow ? Number(incomeRow.total || 0) : 0;
				const totalExpense = Object.prototype.hasOwnProperty.call(expenseTotalsByBranch, branchId)
					? Number(expenseTotalsByBranch[branchId] || 0)
					: 0;
				const difference = totalIncome - totalExpense;
				const expenseRatio =
					totalIncome > 0
						? Math.min(Math.round((totalExpense / totalIncome) * 100), 100)
						: 0;

				return {
					branchId,
					branchName: branchMap[branchId],
					totalIncome,
					totalExpense,
					difference,
					expenseRatio
				};
			});
		}
	},
	methods: {
        toISODate(date) {
            return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
        },
		toggleEmployeeCard(employeeId) {
			const id = Number(employeeId || 0);
			if (id <= 0) return;
			if (this.expandedEmployeeIds.includes(id)) {
				this.expandedEmployeeIds = this.expandedEmployeeIds.filter((x) => x !== id);
				return;
			}
			this.expandedEmployeeIds = [...this.expandedEmployeeIds, id];
		},
		isEmployeeExpanded(employeeId) {
			return this.expandedEmployeeIds.includes(Number(employeeId || 0));
		},
		activeLangCode() {
			if (this.$cookies.get(this.$store.state.LangCooki) != null) {
				const cookieLang = this.$cookies.get(this.$store.state.LangCooki);
				if (cookieLang === 'en' || cookieLang === 'ur') {
					return cookieLang;
				}
				return 'ar';
			}
			if (this.$store.state.deflang === 'en' || this.$store.state.deflang === 'ur') {
				return this.$store.state.deflang;
			}
			return 'ar';
		},
		dashboardApiPath() {
			const base =
				this.$store &&
				this.$store.state &&
				this.$store.state.SAMCOTEC &&
				this.$store.state.SAMCOTEC.r_path
					? this.$store.state.SAMCOTEC.r_path
					: '';
			if (base && base.indexOf('index.php') !== -1) {
				return base.replace('index.php', 'new-dashboard.php');
			}
			if (base && base.endsWith('/')) {
				return `${base}new-dashboard.php`;
			}
			return 'api/new-dashboard.php';
		},
		fetchIncomeData() {
            const serial = ++this.requestSerial;
            this.loadError = false;
			const post = new FormData();
			post.append('module', 'finance');
			post.append('startDate', this.toISODate(this.normalizedDateRange.start));
			post.append('endDate', this.toISODate(this.normalizedDateRange.end));
			post.append('timeGrain', this.timeGrain);
			post.append('branchIds', this.selectedBranchIds.join(','));
			post.append('lang', this.activeLangCode());

			this.incomeLoading = true;

			return axios.post(this.dashboardApiPath(), post)
				.then((response) => {
                    if (serial !== this.requestSerial) return;
					const payload = response && response.data ? response.data : {};
                    if (Number(payload.status) !== 1 || !payload.data) throw new Error('Dashboard request failed');
					if (payload.status !== 1 || !payload.data) {
						this.apiIncomePeriods = [];
						this.apiIncomeTableRows = [];
						this.apiExpenseBreakdown = [];
						this.apiTotalExpenses = null;
						this.apiBranchExpenseTotals = [];
						this.apiFinanceSummary = null;
						this.apiPaymentTypeSummary = [];
						this.apiEmployeeFinanceCards = [];
						return;
					}

					const periods = Array.isArray(payload.data.incomePeriods) ? payload.data.incomePeriods : [];
					const rows = Array.isArray(payload.data.incomeTableRows) ? payload.data.incomeTableRows : [];
					const expenseBreakdown = Array.isArray(payload.data.expenseBreakdown)
						? payload.data.expenseBreakdown
						: [];
					const branchExpenseTotals = Array.isArray(payload.data.branchExpenseTotals)
						? payload.data.branchExpenseTotals
						: [];
					const financeSummary = payload.data.financeSummary && typeof payload.data.financeSummary === 'object'
						? payload.data.financeSummary
						: null;
					const paymentTypeSummary = Array.isArray(payload.data.paymentTypeSummary)
						? payload.data.paymentTypeSummary
						: [];
					const employeeFinanceCards = Array.isArray(payload.data.employeeFinanceCards)
						? payload.data.employeeFinanceCards
						: [];

					this.apiIncomePeriods = periods.map((period, idx) => ({
						key: period.key || `period_${idx}`,
						label: period.label || `P${idx + 1}`
					}));

					this.apiIncomeTableRows = rows.map((row) => {
						const normalized = {
							branch: row.branch || this.lang.nd_unknown_branch,
							branchId: Number(row.branchId || row.branchid || 0),
							total: Number(row.total || 0)
						};

						this.apiIncomePeriods.forEach((period) => {
							normalized[period.key] = Number(row[period.key] || 0);
						});

						return normalized;
					});

					this.apiExpenseBreakdown = expenseBreakdown.map((item) => ({
						type: item.type || this.lang.nd_unknown,
						amount: Number(item.amount || 0),
						percentage: Number(item.percentage || 0)
					}));

					this.apiTotalExpenses = payload.data.totalExpenses !== undefined
						? Number(payload.data.totalExpenses || 0)
						: null;

					this.apiBranchExpenseTotals = branchExpenseTotals.map((row) => ({
						branchId: Number(row.branchId || row.branchid || 0),
						branch: row.branch || this.lang.nd_unknown_branch,
						totalExpense: Number(row.totalExpense || row.total_expense || 0)
					}));

					this.apiFinanceSummary = financeSummary ? {
						totalIncome: Number(financeSummary.totalIncome || 0),
						totalExpenses: Number(financeSummary.totalExpenses || 0),
						totalSalaries: Number(financeSummary.totalSalaries || 0),
						totalPurchase: Number(financeSummary.totalPurchase || 0),
						totalRevenueOrLoss: Number(financeSummary.totalRevenueOrLoss || 0)
					} : null;

					this.apiPaymentTypeSummary = paymentTypeSummary.map((payment) => ({
						id: Number(payment.id || 0),
						name: payment.name || this.lang.nd_unknown,
						income: Number(payment.income || 0),
						expense: Number(payment.expense || 0),
						balance: Number(payment.balance || 0)
					}));

					this.apiEmployeeFinanceCards = employeeFinanceCards.map((employee) => {
						const salary = Number(employee.salary || 0);
						const payAdvanced = Number(employee.payAdvanced || employee.pay_advanced || 0);
						const rewards = Number(employee.rewards || 0);
							const overtime = Number(employee.overtime || 0);
							const nonAttendance = Number(employee.nonAttendance || employee.non_attendance || 0);
						const deductions = Number(employee.deductions || 0);
						const netPaidSalary = employee.netPaidSalary !== undefined
							? Number(employee.netPaidSalary || 0)
								: Number(salary - payAdvanced - deductions - nonAttendance + rewards + overtime);

						return {
							id: Number(employee.id || 0),
							name: employee.name || employee.employee_name || this.lang.nd_unknown,
							branchId: Number(employee.branchId || employee.branchid || 0),
							branchName: employee.branchName || employee.branch || this.lang.nd_unknown_branch,
							salary,
							payAdvanced,
							rewards,
								overtime,
								nonAttendance,
							deductions,
							netPaidSalary,
							idExpiryDate: employee.idExpiryDate || employee.id_expire_date || ''
						};
					});

					const validIds = new Set(this.apiEmployeeFinanceCards.map((employee) => Number(employee.id || 0)));
					this.expandedEmployeeIds = this.expandedEmployeeIds.filter((id) => validIds.has(id));
				})
				.catch(() => {
                    if (serial !== this.requestSerial) return;
                    this.loadError = true;
					this.apiIncomePeriods = [];
					this.apiIncomeTableRows = [];
					this.apiExpenseBreakdown = [];
					this.apiTotalExpenses = null;
					this.apiBranchExpenseTotals = [];
					this.apiFinanceSummary = null;
					this.apiPaymentTypeSummary = [];
					this.apiEmployeeFinanceCards = [];
					this.expandedEmployeeIds = [];
				})
				.finally(() => {
                    if (serial !== this.requestSerial) return;
					this.incomeLoading = false;
				});
		},
		parseISODate(value) {
			if (!value) return null;
			const [year, month, day] = value.split('-').map(Number);
			if (!year || !month || !day) return null;
			return new Date(year, month - 1, day);
		},
		isSameWeek(start, end) {
			const startOfWeek = (date) => {
				const copy = new Date(date);
				const day = copy.getDay();
				const diff = day === 0 ? -6 : 1 - day;
				copy.setDate(copy.getDate() + diff);
				copy.setHours(0, 0, 0, 0);
				return copy;
			};

			return startOfWeek(start).getTime() === startOfWeek(end).getTime();
		},
		generatePeriodsByGrain(start, end, grain) {
			const periods = [];
			let cursor = new Date(start);

			if (grain === 'monthly') {
				cursor = new Date(start.getFullYear(), start.getMonth(), 1);
				while (cursor <= end) {
					const label = cursor.toLocaleDateString('en-US', {
						month: 'short',
						year: 'numeric'
					});
					periods.push({ key: `period_${periods.length}`, label });
					cursor.setMonth(cursor.getMonth() + 1);
				}
				return periods;
			}

			if (grain === 'weekly') {
				while (cursor <= end) {
					const weekEnd = new Date(cursor);
					weekEnd.setDate(weekEnd.getDate() + 6);
					if (weekEnd > end) weekEnd.setTime(end.getTime());

					const label = `W${periods.length + 1} (${cursor.toLocaleDateString('en-US', {
						month: '2-digit',
						day: '2-digit'
					})} - ${weekEnd.toLocaleDateString('en-US', {
						month: '2-digit',
						day: '2-digit'
					})})`;

					periods.push({ key: `period_${periods.length}`, label });
					cursor.setDate(cursor.getDate() + 7);
				}
				return periods;
			}

			while (cursor <= end) {
				const label = cursor.toLocaleDateString('en-US', {
					month: 'short',
					day: '2-digit'
				});
				periods.push({ key: `period_${periods.length}`, label });
				cursor.setDate(cursor.getDate() + 1);
			}

			return periods;
		},
		getIdExpiryMeta(idExpiryDate) {
			const expiry = this.parseISODate(idExpiryDate);
			if (!expiry) {
				return {
					label: this.lang.nd_no_date,
					className: 'status-default'
				};
			}

			const now = new Date();
			now.setHours(0, 0, 0, 0);
			const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

			if (diffDays < 0) {
				return {
					label: this.lang.nd_expired,
					className: 'status-expired'
				};
			}

			if (diffDays <= 30) {
				return {
					label: `${this.lang.nd_expiring} (${diffDays}d)`,
					className: 'status-warning'
				};
			}

			return {
				label: this.lang.nd_valid,
				className: 'status-valid'
			};
		},
		currencyLocale() {
			const langCode = this.activeLangCode();
			if (langCode === 'ar') return 'ar-SA';
			if (langCode === 'ur') return 'ur-PK';
			return 'en-US';
		},
		formatCurrency(value) {
			const amount = Number(value || 0);
			return new Intl.NumberFormat(this.currencyLocale(), {
				style: 'currency',
				currency: 'SAR',
				maximumFractionDigits: 2
			}).format(amount);
		}
	}
};
</script>

<style scoped>
.module-title {
	margin-bottom: 8px;
	font-size: 1rem;
	font-weight: 700;
	color: #2d3748;
}

.section-card {
	border: 1px solid #e6ecf3;
	border-radius: 10px;
	padding: 14px;
	background: #ffffff;
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 12px;
}

.section-title {
	font-size: 0.95rem;
	font-weight: 700;
	color: #2d3748;
}

.kpi-card {
	padding: 12px;
	border-radius: 10px;
	color: #fff;
	min-height: 88px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	margin-bottom: 10px;
}

.kpi-label {
	font-size: 0.78rem;
	font-weight: 600;
	opacity: 0.95;
}

.kpi-value {
	font-size: 1rem;
	font-weight: 700;
}

.kpi-card--income { background: linear-gradient(135deg, #1d4ed8, #2563eb); }
.kpi-card--expense { background: linear-gradient(135deg, #b91c1c, #dc2626); }
.kpi-card--salary { background: linear-gradient(135deg, #7c3aed, #8b5cf6); }
.kpi-card--purchase { background: linear-gradient(135deg, #0f766e, #14b8a6); }
.kpi-card--profit { background: linear-gradient(135deg, #047857, #16a34a); }
.kpi-card--loss { background: linear-gradient(135deg, #b91c1c, #ef4444); }

.payment-card {
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 10px;
	margin-bottom: 10px;
	background: #fff;
}

.payment-types-line {
	display: flex;
	flex-wrap: nowrap;
	gap: 10px;
	overflow-x: auto;
	padding-bottom: 4px;
}

.payment-types-line .payment-card {
	margin-bottom: 0;
}

.payment-title {
	font-size: 0.85rem;
	font-weight: 700;
	margin-bottom: 8px;
	color: #1e293b;
}

.payment-row {
	display: flex;
	justify-content: space-between;
	font-size: 0.8rem;
	margin-bottom: 4px;
	color: #334155;
}

.payment-row--balance {
	padding-top: 6px;
	margin-top: 6px;
	border-top: 1px dashed #cbd5e1;
	font-weight: 700;
}

.payment-card--profit { border-inline-start: 4px solid #16a34a; }
.payment-card--loss { border-inline-start: 4px solid #ef4444; }
.payment-card--even { border-inline-start: 4px solid #f59e0b; }

.finance-table {
	border: 1px solid #edf1f6;
	border-radius: 8px;
}

.finance-table ::v-deep th {
	background: #f8fafc;
	font-weight: 700;
	color: #4a5568;
	font-size: 0.8rem;
}

.expense-row {
	margin-bottom: 11px;
}

.expense-type {
	font-size: 0.85rem;
	font-weight: 600;
	color: #4a5568;
}

.expense-value {
	font-size: 0.85rem;
	font-weight: 700;
	color: #1f2937;
}

.employee-card {
	border-color: #e3e8ef !important;
	padding: 12px;
	margin-bottom: 12px;
	border-radius: 10px;
	background: #ffffff;
}

.employee-card-head {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	border: 0;
	background: transparent;
	padding: 0;
	text-align: start;
	margin-bottom: 8px;
}

.employee-head-right {
	display: flex;
	align-items: center;
	gap: 8px;
	color: #1f2937;
}

.employee-total {
	font-size: 0.8rem;
	white-space: nowrap;
	color: #334155;
}

.employee-name {
	font-size: 0.9rem;
	font-weight: 700;
	color: #1f2937;
}

.employee-branch {
	font-size: 0.78rem;
	color: #6b7280;
}

.status-chip {
	font-size: 0.72rem;
	font-weight: 700;
	padding: 0 8px !important;
	border-radius: 999px;
}

.metric-row {
	display: flex;
	justify-content: space-between;
	margin-bottom: 6px;
	font-size: 0.82rem;
	color: #4b5563;
}

.metric-label {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.metric-help-icon {
	font-size: 0.9rem !important;
	color: #6b7280;
	cursor: pointer;
}

.metric-row--net {
	padding-top: 8px;
	margin-top: 8px;
	border-top: 1px dashed #dbe2ea;
	font-weight: 700;
	color: #111827;
}

.summary-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16px;
	padding: 10px 0;
	border-top: 1px solid #edf1f6;
}

.summary-row:first-of-type {
	border-top: 0;
}

.summary-main {
	flex: 1;
}

.summary-branch {
	font-size: 0.88rem;
	font-weight: 700;
	color: #1f2937;
	margin-bottom: 2px;
}

.summary-values {
	display: flex;
	gap: 14px;
	font-size: 0.8rem;
	color: #4b5563;
}

.summary-indicator-wrap {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 270px;
}

.summary-indicator-wrap .v-progress-linear {
	min-width: 165px;
}

.status-warning {
	background: #fdecea !important;
	color: #c62828 !important;
}

.status-expired {
	background: #f6d9de !important;
	color: #7f1d1d !important;
}

.status-valid {
	background: #e9f7ef !important;
	color: #1b5e20 !important;
}

.status-default {
	background: #eef2f7 !important;
	color: #475569 !important;
}

@media (max-width: 991px) {
	.summary-row {
		flex-direction: column;
		align-items: flex-start;
	}

	.summary-indicator-wrap {
		width: 100%;
		min-width: 100%;
	}
}
</style>
